// PlatformModules — super_admin module enablement matrix.
//
// /dashboard/platform/modules — gated by platform:execute.impersonate.
// Lists tenants in a left picker; clicking one loads its module
// enablement state on the right with toggle switches. Each toggle
// POSTs immediately (no batch save) so there's no "unsaved changes"
// management. Audit row `module.enabled` / `module.disabled`
// written server-side per toggle.
//
// Tenant module enablement is a SENTRY-side ops decision (docs/
// specs/module-platform.md §4, see PR description for the rationale).
// tenant_admin should NOT see this page; the page itself is route-
// gated by RequireRole + the backend by `platform:execute.impersonate`.

import React, { useEffect, useState } from "react";
import { Puzzle, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { apiFetch } from "@nexus/frontend-shared/api-client";
import PageHeader from "@nexus/frontend-shared/components/PageHeader";
import Skeleton from "@nexus/frontend-shared/components/Skeleton";
import EmptyState from "@nexus/frontend-shared/components/EmptyState";

interface TenantRow {
  id: string;
  name: string;
  slug: string;
}

interface ModuleRow {
  id: string;
  display_name: string;
  description: string;
  enabled: boolean;
  enabled_at: string | null;
  enabled_by_user_id: string | null;
}

interface TenantModulesResponse {
  tenant_id: string;
  tenant_name: string;
  modules: ModuleRow[];
}

type TenantsState =
  | { kind: "loading" }
  | { kind: "ready"; rows: TenantRow[] }
  | { kind: "error"; message: string };

type ModulesState =
  | { kind: "idle" }
  | { kind: "loading"; tenantId: string }
  | { kind: "ready"; data: TenantModulesResponse }
  | { kind: "error"; message: string };

export default function PlatformModules(): React.ReactElement {
  const [tenantsState, setTenantsState] = useState<TenantsState>({ kind: "loading" });
  const [modulesState, setModulesState] = useState<ModulesState>({ kind: "idle" });
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  /** Per-module saving flag — keyed by module id. Lets the row's
   *  toggle show a spinner while its POST is in flight. */
  const [savingModule, setSavingModule] = useState<string | null>(null);

  // Load tenants list once.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await apiFetch("/v1/platform/tenants");
        if (!r.ok) {
          if (!cancelled) setTenantsState({ kind: "error", message: `HTTP ${r.status}` });
          return;
        }
        const body = (await r.json()) as { items: TenantRow[] };
        if (cancelled) return;
        // Exclude `_platform` pseudo-tenant — module enablement is
        // a per-customer concept, not a platform-level setting.
        const rows = (body.items ?? []).filter((t) => t.id !== "_platform");
        setTenantsState({ kind: "ready", rows });
        if (rows.length > 0 && !selectedTenant) {
          setSelectedTenant(rows[0].id);
        }
      } catch (err) {
        if (!cancelled) {
          setTenantsState({
            kind: "error",
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load module enablement when selected tenant changes.
  useEffect(() => {
    if (!selectedTenant) return;
    let cancelled = false;
    void (async () => {
      setModulesState({ kind: "loading", tenantId: selectedTenant });
      try {
        const r = await apiFetch(`/v1/platform/modules/tenants/${encodeURIComponent(selectedTenant)}`);
        if (!r.ok) {
          if (!cancelled) setModulesState({ kind: "error", message: `HTTP ${r.status}` });
          return;
        }
        const body = (await r.json()) as TenantModulesResponse;
        if (cancelled) return;
        setModulesState({ kind: "ready", data: body });
      } catch (err) {
        if (!cancelled) {
          setModulesState({
            kind: "error",
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTenant]);

  async function toggle(moduleId: string, nextEnabled: boolean): Promise<void> {
    if (!selectedTenant || modulesState.kind !== "ready") return;
    setSavingModule(moduleId);
    try {
      const r = await apiFetch(
        `/v1/platform/modules/tenants/${encodeURIComponent(selectedTenant)}/${encodeURIComponent(moduleId)}`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ enabled: nextEnabled }),
        },
      );
      if (!r.ok) {
        let detail = "";
        try { detail = JSON.stringify(await r.json()); } catch { /* ignore */ }
        alert(`切換失敗：HTTP ${r.status}${detail ? `\n${detail}` : ""}`);
        return;
      }
      // Optimistic local update — keeps the UI snappy without a full
      // refetch. The server's idempotency check would no-op anyway
      // if the user double-clicks.
      setModulesState({
        kind: "ready",
        data: {
          ...modulesState.data,
          modules: modulesState.data.modules.map((m) =>
            m.id === moduleId
              ? {
                  ...m,
                  enabled: nextEnabled,
                  enabled_at: nextEnabled ? new Date().toISOString() : m.enabled_at,
                }
              : m,
          ),
        },
      });
    } catch (err) {
      alert(`切換失敗：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSavingModule(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="模組啟用"
        description="決定每個客戶單位（tenant）可以使用哪些模組。設定立即生效；變動會寫入 audit log（module.enabled / module.disabled）。"
      />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Tenant picker */}
        <div className="rounded-xl border border-border bg-bg-surface overflow-hidden">
          <div className="px-3 py-2 border-b border-border text-2xs font-medium text-text-muted">
            客戶單位（tenant）
          </div>
          {tenantsState.kind === "loading" && (
            <div className="p-3 space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          )}
          {tenantsState.kind === "error" && (
            <div className="p-3 text-2xs text-danger">載入失敗：{tenantsState.message}</div>
          )}
          {tenantsState.kind === "ready" && (
            <div className="max-h-[600px] overflow-y-auto">
              {tenantsState.rows.length === 0 && (
                <div className="p-3 text-2xs text-text-muted">沒有客戶單位</div>
              )}
              {tenantsState.rows.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedTenant(t.id)}
                  className={[
                    "w-full text-left px-3 py-2 text-2xs border-b border-border last:border-b-0",
                    selectedTenant === t.id
                      ? "bg-accent/15 text-accent"
                      : "text-text-primary hover:bg-bg-hover",
                  ].join(" ")}
                >
                  <div className="font-medium truncate">{t.name}</div>
                  <div className="text-text-muted font-mono text-[10px] truncate">{t.id}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Modules for selected tenant */}
        <div>
          {modulesState.kind === "idle" && (
            <EmptyState
              icon={Puzzle}
              title="請選擇一個客戶單位"
              description="左側點選 tenant 後即可管理該客戶可用的模組。"
            />
          )}
          {modulesState.kind === "loading" && (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}
          {modulesState.kind === "error" && (
            <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-2xs text-danger">
              載入失敗：{modulesState.message}
            </div>
          )}
          {modulesState.kind === "ready" && (
            <div className="rounded-xl border border-border bg-bg-surface divide-y divide-border">
              {modulesState.data.modules.length === 0 && (
                <div className="p-4 text-2xs text-text-muted">平台目前沒有任何模組</div>
              )}
              {modulesState.data.modules.map((m) => {
                const saving = savingModule === m.id;
                return (
                  <div key={m.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                        {m.enabled ? (
                          <CheckCircle2 className="h-4 w-4 text-success" />
                        ) : (
                          <XCircle className="h-4 w-4 text-text-muted" />
                        )}
                        <span>{m.display_name}</span>
                        <code className="font-mono text-2xs text-text-muted">{m.id}</code>
                      </div>
                      <div className="text-2xs text-text-muted mt-0.5">{m.description}</div>
                      {m.enabled && m.enabled_at && (
                        <div className="text-[10px] text-text-muted mt-0.5">
                          啟用於 {new Date(m.enabled_at).toLocaleString("zh-TW", { timeZone: "Asia/Taipei" })}
                          {m.enabled_by_user_id && (
                            <> · 由 <code className="font-mono">{m.enabled_by_user_id}</code></>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void toggle(m.id, !m.enabled)}
                      className={[
                        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-2xs font-medium self-end sm:self-auto",
                        "disabled:opacity-40 disabled:cursor-not-allowed",
                        m.enabled
                          ? "bg-danger/15 text-danger hover:bg-danger/25"
                          : "bg-success/15 text-success hover:bg-success/25",
                      ].join(" ")}
                    >
                      {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      {m.enabled ? "停用" : "啟用"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
